import { HttpErrorIgnore, HttpErrorUpsert, HttpUpsert200 } from '../../../../global';
import apiUtil from '../../../core/utils/apiUtil';
import apiSvc, { BinaryFileType } from '../../../core/services/apiSvc';
import utilSvc from '../../../core/utilSvc';
import validSvc from '../../../core/validSvc';

// provider imports
import { PROVIDER_LIST_EXCEL_FILE_NAME } from '../provider_const';
import { ProviderDataItem } from '../list/provider_list_types';
import { AdminProviderSearchModel } from '../list/provider_list_types';
import { AdminProviderModel } from '../edit/provider_edit_types';

// read
const GET_LIST = 'admin/provider/list';
const GET_DETAIL = 'admin/provider/get';
const GET_EXCEL = 'admin/provider/excel';
const GET_TAXONOMY_LIST = 'admin/provider/taxonomy/list';

// write
const UPDATE_PROVIDER = 'admin/provider/save';

// strongly type API request
type ApiListQuery = {
    CE: string[];
    NPI: string;
    FullName: string;
    Validated: boolean;
    FaxConsent: boolean;
};
// convert API response into ProviderDataItem
function providerListApiToVM(data: object): ProviderDataItem[] {
    const items = Array.isArray(data) ? (data as ProviderDataItem[]) : [];

    // do this once per API call rather than on every render
    items.forEach(item => {
        item.FaxConsent = item.FaxConsent ? 'YES' : 'NO';
        item.CEs = apiUtil.arrayToJoinedString(item.CEs, ', ');
        item.Validated = item.Validated ? 'YES' : 'NO';
    });

    return items;
}

async function getList(query: AdminProviderSearchModel): Promise<ProviderDataItem[] | HttpErrorIgnore> {
    // params?
    // what were the Angular param checks? you can put them in here as-is

    const apiRequest: ApiListQuery = {
        CE: query.CE.val,
        NPI: query.NPI.checked ? query.NPI.val : '',
        FullName: query.FullName.checked ? query.FullName.val : '',
        Validated: !!query.Validated.val,
        FaxConsent: !!query.FaxConsent.val,
    };

    try {
        const r = await apiSvc.getEntityList<ProviderDataItem>(GET_LIST, { Params: apiRequest });
        return providerListApiToVM(r);
    } catch (err: any) {
        // all entity list errors handled upstream
        return utilSvc.http.ignore(err, 'providerRepo.getList') as HttpErrorIgnore;
    }
}

async function getExcel(): Promise<HttpErrorIgnore | undefined> {
    return apiSvc.postGetBinary(GET_EXCEL, {}, PROVIDER_LIST_EXCEL_FILE_NAME, BinaryFileType.Excel);
}

async function getDetail(id: string): Promise<object> {
    // params
    if (!validSvc.isText(id)) {
        throw new Error('providerRepo.getDetail, id is required');
    }

    return apiSvc.getEntity(GET_DETAIL, {
        Params: {
            Id: id,
        },
    });
}

async function getTaxonomyList(): Promise<any[]> {
    return apiSvc.getEntityList(GET_TAXONOMY_LIST, {});
}

async function upsert(action: string, m: AdminProviderModel): Promise<HttpUpsert200 | HttpErrorUpsert> {
    return apiSvc.postModel(UPDATE_PROVIDER, providerModelAPI(m));
}

type ProviderModelAPI = {
    Entity: {
        Id: string;
        Address1: string;
        Address2: string;
        City: string;
        Fax: string;
        FaxAttempts: string;
        FaxConsent: boolean;
        FaxFrequency: string;
        NPI: string;
        Phone: string;
        PracticeName: string;
        PrimaryTaxonomy: string;
        State: string;
        Validated: boolean;
        Zip: string;
    };
};
function providerModelAPI(m: AdminProviderModel): ProviderModelAPI {
    return {
        Entity: {
            Id: m.Id,
            Address1: m.Address1.val,
            Address2: m.Address2.val,
            City: m.City.val,
            Fax: m.Fax.val,
            FaxAttempts: m.FaxAttempts.val,
            FaxConsent: convertStringToBoolean(m.FaxConsent.val),
            FaxFrequency: m.FaxFrequency.val,
            NPI: m.NPI.val,
            Phone: m.Phone.val,
            PracticeName: m.PracticeName.val,
            PrimaryTaxonomy: m.PrimaryTaxonomy.val,
            State: m.State.val,
            Validated: convertStringToBoolean(m.Validated.val),
            Zip: m.Zip.val,
        },
    };
}

function convertStringToBoolean(val: string): boolean {
    if (val === 'true') {
        return true;
    } else if (val === 'false') {
        return false;
    }
    return false;
}

async function preloadCeOptions(): Promise<string[]> {
    try {
        const r = await apiSvc.getEntityList('ops/lookup/cereferral', {});
        return r as string[];
    } catch (err: any) {
        return [];
    }
}

export const providerRepo = {
    getList,
    getExcel,
    getDetail,
    preloadCeOptions,
    getTaxonomyList,
    upsert,
};
export default providerRepo;
